import { useCallback, useEffect } from 'react';
import cn from 'classnames';
import { Button, BUTTON_THEMES } from 'ht-styleguide';
import PlainButton from '@components/UI/PlainButton';
/* Home Hub Store */
import {
  useHomeHubStoreAnimationIsFinished,
  useHomeHubStoreHasDirtyAnswerSelections,
  useHomeHubStoreSelectedAnswersClientTransient,
  useHomeHubStoreShowSurveyFooter,
} from '@scenes/HomeHub/store/client/homehub.store';

/* Queries & Mutations */
import useHomeHubMutationUpdateHubSurvey from '@scenes/HomeHub/store/queries/useHomeHubMutation.updateHubSurvey';

/* Hooks and Selectors */
import { useIsSessionLoadedSelector } from '@store/queries/user/useUser.selector';
import {
  useAnswerCompletePercentage,
  useHomeHubHasSelectedAnswersSelector,
  useHomeHubQuestionSelector,
} from '@scenes/HomeHub/store/queries/useHomeHub.selectors';
import useRouteController from '@scenes/HomeHub/hooks/hook.routeController';

/* Components */
import ProgressBar from '@components/UI/ProgressBar';

import { IHash } from 'types/base.types';
import styles from './homehub.footer.module.scss';

const ButtonCta = 'Continue';

/**
 * Footer for the Home Hub - we are controlling the mutations and routing here via controllers,
 * but an argument can be made to move this logic out. For now, this footer does only this, so
 * no harm. If this footer grows in intent, we should move this logic out.
 *
 * @returns {JSX.Element | null}
 * @constructor
 */
const HomeHubFooter = () => {
  /* Store */
  const hasDirtyAnswerSelections = useHomeHubStoreHasDirtyAnswerSelections();
  const selectedAnswersClientTransient = useHomeHubStoreSelectedAnswersClientTransient();
  const animationIsFinished = useHomeHubStoreAnimationIsFinished();
  const showSurveyFooter = useHomeHubStoreShowSurveyFooter();

  /* Get current suppress attribute hook */
  const { nextQuestionRoute } = useRouteController();

  /* Query/Client Selectors */
  const hasUserLoaded = useIsSessionLoadedSelector();
  const currentQuestion = useHomeHubQuestionSelector();
  const answerPercentage = useAnswerCompletePercentage(true);
  const hasPreviousSubmittedAnswers = useHomeHubHasSelectedAnswersSelector();

  /* Mutation */
  const { mutate: updateSurvey, isPending, isSuccess } = useHomeHubMutationUpdateHubSurvey();

  /* Get current question answers hook */
  const hasAnswers = selectedAnswersClientTransient.length > 0;

  /* Get current require attribute hook */
  const isRequired = currentQuestion?.required || hasPreviousSubmittedAnswers;

  /* ------------- METHODS ------------------ */
  const goToNextQuestion = useCallback(() => {
    nextQuestionRoute();
  }, [nextQuestionRoute]);
  /*
    User has selected answers, and a continue click will reformat them and send them up.
    Only format and fire if we have a dirty form. No reason to resubmit the same data.

    1. Pre-seeded, continue will be enabled.
       a. user interacted with form, and we have dirty selections
          i. hit api.
       b. continue w/o submitting previous answers

       UPDATE: We submit all answers, even previously submitted ones. The reason is because we do not "really" allow
               edits. Once a user updates an already completed survey, ALL other answers to all the questions are reset.
               They want to keep historical data of "completed surveys".
  */
  const onHandleButtonSubmit = () => {
    if (hasDirtyAnswerSelections) {
      const params = currentQuestion.answerSchemaNormalizer.params(selectedAnswersClientTransient);
      updateSurvey(params);
    } else {
      goToNextQuestion();
    }
  };

  /* Button Wrapper */
  const getButton = (props: IHash<string | boolean>, name: string) => (
    <Button {...props} onClick={onHandleButtonSubmit}>
      {name}
    </Button>
  );

  /* Gives back the button state and type */
  const renderButtonType = () =>
    (() => {
      switch (true) {
        case Boolean(isRequired && !hasAnswers):
          return getButton({ theme: BUTTON_THEMES.PRIMARY, disabled: true }, ButtonCta);
        case Boolean(!isRequired && !hasAnswers):
          return <PlainButton onClickHandler={goToNextQuestion}>Skip for now</PlainButton>;
        case Boolean(isRequired && hasAnswers):
        case Boolean(!isRequired && hasAnswers):
          return getButton({ theme: BUTTON_THEMES.PRIMARY, disabled: isPending }, ButtonCta);
        default:
          return null;
      }
    })();

  /* On a successful update, lets forward on. Disable the continue button */
  useEffect(() => {
    // REDIRECT TO NEXT QUESTION
    if (isSuccess) {
      goToNextQuestion();
    }
  }, [isSuccess, goToNextQuestion]);
  /* Suppress Footer */
  if (!showSurveyFooter || !hasUserLoaded || !currentQuestion || currentQuestion.non_question_type) return null;

  return (
    <>
      <div className={cn(styles.containerFooter, animationIsFinished && 'positionFixed')}>
        {renderButtonType()}
        <ProgressBar percentage={answerPercentage} classNameType="baseDisplayWithPercentage" />
      </div>
    </>
  );
};

export default HomeHubFooter;
